// redux/vaultQuestion/vaultQuestionThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getVaultQuestionById,
  fetchVaultQuestions,
  createVaultQuestion,
  updateVaultQuestionById,
  deleteVaultQuestion,
  changeVaultQuestionStatus,
  flagVaultQuestion,
  deleteVaultQuestionsBulk,
  exportVaultQuestions,
  saveBulkVaultQuestionsService,
  fetchBulkVaultQuestionsService,
  aiGenerateQuestions,
  aiReviewQuestion,
  bulkAIReview,
  publishVaultQuestion,
  bulkPublishVaultQuestions,
} from "../../components/services/vaultQuestionService";
import { handleUnauthorized } from "../../utils/apiHelper";
import axios from "axios";

export const fetchVaultQuestionById = createAsyncThunk(
  "vaultQuestion/fetchById",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      const { data } = await getVaultQuestionById(id);
      return data?.data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to fetch question");
    }
  }
);

export const fetchAllVaultQuestions = createAsyncThunk(
  "vaultQuestion/fetchAll",
  async ({ params, navigate }, { getState, rejectWithValue }) => {
    try {
      const { filters } = getState().vaultQuestion;
      const mergedParams = { ...filters, ...params };
      const { data } = await fetchVaultQuestions(mergedParams);
      return data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to fetch questions");
    }
  }
);

export const createVaultQuestionThunk = createAsyncThunk(
  "vaultQuestion/add",
  async ({ data, navigate }, { rejectWithValue }) => {
    try {
      const response = await createVaultQuestion(data);
      return response?.data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to add question");
    }
  }
);

export const updateVaultQuestionThunk = createAsyncThunk(
  "vaultQuestion/update",
  async ({ id, data, navigate }, { rejectWithValue }) => {
    try {
      const response = await updateVaultQuestionById(id, data);
      return response.data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to update question");
    }
  }
);

export const toggleVaultQuestionStatus = createAsyncThunk(
  "vaultQuestion/changeStatus",
  async ({ id, data, navigate }, { rejectWithValue }) => {
    try {
      const res = await changeVaultQuestionStatus(id, data);
      return res.data;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to change status");
    }
  }
);

export const deleteVaultQuestionById = createAsyncThunk(
  "vaultQuestions/delete",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      const response = await deleteVaultQuestion(id);
      return id;
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete question"
      );
    }
  }
);

export const flagVaultQuestionThunk = createAsyncThunk(
  "vaultQuestion/flag",
  async ({ id, data, navigate }, { rejectWithValue }) => {
    try {
      await flagVaultQuestion(id, data); // ✅ fix wrong reference
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(err.message || "Failed to flag question");
    }
  }
);

export const deleteVaultQuestionsBulkByIds = createAsyncThunk(
  "vaultQuestions/deleteBulk",
  async ({ ids, navigate }, { rejectWithValue }) => {
    try {
      const response = await deleteVaultQuestionsBulk(ids);
      return ids; // Return array of deleted IDs
    } catch (err) {
      if (err.response?.status === 401) handleUnauthorized(navigate);
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to delete selected questions"
      );
    }
  }
);

export const fetchVaultQuestionsExport = createAsyncThunk(
  "vaultQuestions/export",
  async ({ format = "csv", filters = {} }, { rejectWithValue }) => {
    try {
      const params = { format, ...filters };
      const res = await exportVaultQuestions(params);

      // Trigger browser download
      const blob = new Blob([res.data], {
        type:
          format === "xlsx"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv;charset=utf-8;",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        format === "xlsx" ? "vault_questions.xlsx" : "vault_questions.csv"
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return true; // success flag
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const saveBulkVaultQuestions = createAsyncThunk(
  "vaultQuestion/saveBulkVaultQuestions",
  async (data, { rejectWithValue }) => {
    try {
      const response = await saveBulkVaultQuestionsService(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const fetchBulkVaultQuestions = createAsyncThunk(
  "vaultQuestion/fetchBulkVaultQuestions",
  async (ids, { rejectWithValue }) => {
    try {
      const response = await fetchBulkVaultQuestionsService(ids);
      return response.data; // Expecting { success: true, data: [...] }
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const generateAIQuestions = createAsyncThunk(
  "aiGenerator/fetchAIQuestions",
  async (topic, { rejectWithValue }) => {
    try {
      const data = await aiGenerateQuestions(topic);
      if (data.status === 1) {
        return data.review;
      } else {
        return rejectWithValue(data.message || "Failed to generate questions");
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const fetchAiReview = createAsyncThunk(
  "aiReview/fetchAiReview",
  async (id, { rejectWithValue }) => {
    try {
      const response = await aiReviewQuestion(id);
      return response; // ✅ unwrap JSON
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


// ids is optional array of selected IDs
export const bulkReviewThunk = createAsyncThunk(
  "vaultQuestions/bulkReview",
  async (ids = [], { rejectWithValue }) => {
    try {
      const response = await bulkAIReview(ids);
      return response.data; // { status, message, results }
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);


// Publish a single question
export const publishQuestion = createAsyncThunk(
  "vaultQuestion/publishQuestion",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await publishVaultQuestion(id);
      return response.data; // should return updated question
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Bulk publish questions
export const bulkPublishQuestions = createAsyncThunk(
  "vaultQuestion/bulkPublishQuestions",
  async ({ ids = [] } = {}, { rejectWithValue }) => {
    try {
      const response = await bulkPublishVaultQuestions(ids);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);